import { type RowData } from '@tanstack/react-table';
import { Info } from 'lucide-react';

import { DimeButton } from '../DimeButton.js';
import { DimeTooltip } from '../DimeTooltip.js';
import { cn } from '../lib/utils.js';
import type { TableLoadingMeta } from '../table-internals/table-loading-meta.js';
import { cycleColumnSort } from './cycle-column-sort.js';
import type { Column } from './DimeDataTable.types.js';
import { SortColumnIcon } from './SortColumnIcon.js';

/** Bound structurally, not to DimeTableFeatures, so DimeNestedTable's columns can use it too. */
type SortableColumn = Pick<
  Column<RowData, unknown>,
  'getCanSort' | 'getIsSorted' | 'toggleSorting' | 'clearSorting'
> & { table?: { options: { meta?: TableLoadingMeta } } };

function ColumnHeaderInfoTooltip({
  tooltip,
  tooltipTitle,
}: Readonly<{ tooltip: string; tooltipTitle?: string }>) {
  const content = tooltipTitle ? (
    <div>
      <span className="mb-0.5 block font-semibold">{tooltipTitle}</span>
      {tooltip}
    </div>
  ) : (
    tooltip
  );
  return (
    <DimeTooltip
      content={content}
      {...(tooltipTitle ? { contentClassName: 'w-62 text-left leading-[17px]' } : {})}
    >
      <button
        type="button"
        className="inline-flex appearance-none border-0 bg-transparent p-0 outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50"
        aria-label={tooltipTitle ? `${tooltipTitle}: ${tooltip}` : tooltip}
      >
        <Info className="size-3.5 text-muted-foreground" aria-hidden="true" />
      </button>
    </DimeTooltip>
  );
}

export function DataTableColumnHeader({
  column,
  title,
  tooltip,
  tooltipTitle,
  className,
}: {
  column: SortableColumn;
  title: string;
  tooltip?: string;
  /** Rendered bold above `tooltip`, for a column whose header is an abbreviation or a short form. */
  tooltipTitle?: string;
  className?: string;
}) {
  if (!column.getCanSort()) {
    return (
      <div className={cn('flex items-center gap-1', className)}>
        <span>{title}</span>
        {tooltip && <ColumnHeaderInfoTooltip tooltip={tooltip} tooltipTitle={tooltipTitle} />}
      </div>
    );
  }

  const sorted = column.getIsSorted();
  // A sort issued mid-fetch is superseded before it lands, matching DataTablePagination's gating.
  const isLoading = column.table?.options.meta?.isLoading ?? false;

  return (
    <div className={cn('flex items-center gap-1', className)}>
      <DimeButton
        type="button"
        variant="ghost"
        size="sm"
        className="gap-1.5 px-0 text-sm"
        disabled={isLoading}
        onClick={() => cycleColumnSort(column)}
      >
        <span>{title}</span>
        <SortColumnIcon sorted={sorted} />
      </DimeButton>
      {tooltip && <ColumnHeaderInfoTooltip tooltip={tooltip} tooltipTitle={tooltipTitle} />}
    </div>
  );
}
